/**
 * FocusTrap traps keyboard focus within a container element.
 * When active, Tab and Shift+Tab cycle through focusable elements
 * without allowing focus to escape the container.
 */

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

export class FocusTrap {
  private container: HTMLElement | null;
  private handleKeyDown: ((event: KeyboardEvent) => void) | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
  }

  /**
   * Activates the focus trap. Re-queries focusable elements each time
   * (in case DOM has changed) and attaches a keydown listener for
   * Tab/Shift+Tab cycling.
   */
  activate(): void {
    if (!this.container) return;

    this.deactivate();

    this.handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      const focusableElements = this.container!.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        // Shift+Tab: if on first element, wrap to last
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab: if on last element, wrap to first
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    this.container.addEventListener('keydown', this.handleKeyDown);
  }

  /**
   * Deactivates the focus trap by removing the keydown listener.
   */
  deactivate(): void {
    if (this.container && this.handleKeyDown) {
      this.container.removeEventListener('keydown', this.handleKeyDown);
      this.handleKeyDown = null;
    }
  }

  /**
   * Destroys the focus trap, removing listeners and cleaning up references.
   */
  destroy(): void {
    this.deactivate();
    this.container = null;
  }
}
